import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import axios from 'axios';
import { useSelector } from 'react-redux';

const STATUS_OPTIONS = ['new', 'open', 'pending', 'resolved', 'closed'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

const MAX_ATTACHMENTS = 10;

const statusChipColor = {
    new: 'default',
    open: 'warning',
    pending: 'info',
    resolved: 'success',
    closed: 'default',
};

const priorityChipColor = {
    low: 'success',
    medium: 'primary',
    high: 'warning',
    urgent: 'error',
};

const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const ReplyBubble = ({ reply }) => {
    const isInternal = reply.internal;
    const isAgent = reply.authorType === 'agent';
    const bubbleBg = isInternal ? '#f3e5f5' : isAgent ? '#e1f5fe' : '#f1f8e9';
    const borderColor = isInternal ? '#ab47bc' : isAgent ? '#039be5' : '#7cb342';
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 2,
                borderLeft: `4px solid ${borderColor}`,
                backgroundColor: bubbleBg,
            }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{reply.authorName || 'Support User'}</Typography>
                    {reply.authorEmail && (
                        <Typography variant="caption" color="text.secondary">
                            ({reply.authorEmail})
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: { sm: 'auto' } }}>
                    {isInternal && <Chip size="small" label="Internal note" color="secondary" variant="outlined" />}
                    <Typography variant="caption" color="text.secondary">
                        {formatDateTime(reply.createdAt)}
                    </Typography>
                </Stack>
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', mt: 1 }}>
                {reply.message}
            </Typography>
            {Array.isArray(reply.attachments) && reply.attachments.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                    {reply.attachments.map((attachment) => (
                        <Chip
                            key={`${reply.id}-${attachment.url}`}
                            component="a"
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            label={attachment.name}
                            clickable
                            icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
                            sx={{ maxWidth: 260 }}
                        />
                    ))}
                </Stack>
            )}
        </Paper>
    );
};

const AdminSupportTickets = () => {
    const { token, user } = useSelector((state) => state.auth);

    const [tickets, setTickets] = useState([]);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [ticketsError, setTicketsError] = useState('');

    const [stats, setStats] = useState({ status: {}, priority: {}, total: 0 });
    const [statsLoading, setStatsLoading] = useState(false);

    const [filters, setFilters] = useState({ status: 'all', priority: 'all', assigned: 'all', search: '' });
    const [searchDraft, setSearchDraft] = useState('');

    const [selectedTicketId, setSelectedTicketId] = useState('');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [thread, setThread] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [threadError, setThreadError] = useState('');

    const [replyMessage, setReplyMessage] = useState('');
    const [internalNote, setInternalNote] = useState(false);
    const [assignToSelf, setAssignToSelf] = useState(false);
    const [replySubmitting, setReplySubmitting] = useState(false);
    const [replyError, setReplyError] = useState('');
    const [replyAttachments, setReplyAttachments] = useState([]);
    const [replyAttachmentUploading, setReplyAttachmentUploading] = useState(false);
    const [replyAttachmentError, setReplyAttachmentError] = useState('');

    const [statusUpdate, setStatusUpdate] = useState('');
    const [priorityUpdate, setPriorityUpdate] = useState('');
    const [actionError, setActionError] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

    const uploadAttachment = useCallback(
        async (file) => {
            if (!file) return null;
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await axios.post('/api/support/tickets/attachments', formData, {
                headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
            });
            return data?.attachment || null;
        },
        [authHeaders]
    );

    const handleReplyAttachmentChange = useCallback(
        async (event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = '';
            if (!files.length) return;

            const availableSlots = MAX_ATTACHMENTS - replyAttachments.length;
            if (availableSlots <= 0) {
                setReplyAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
                return;
            }

            let filesToUpload = files;
            if (files.length > availableSlots) {
                filesToUpload = files.slice(0, availableSlots);
                setReplyAttachmentError(
                    `Only ${availableSlots} more attachment${availableSlots === 1 ? '' : 's'} can be added.`
                );
            } else {
                setReplyAttachmentError('');
            }

            setReplyAttachmentUploading(true);
            try {
                const uploaded = [];
                for (const file of filesToUpload) {
                    // eslint-disable-next-line no-await-in-loop
                    const attachment = await uploadAttachment(file);
                    if (attachment) {
                        uploaded.push(attachment);
                    }
                }
                if (uploaded.length) {
                    setReplyAttachments((prev) => [...prev, ...uploaded]);
                }
            } catch (error) {
                const message = error.response?.data?.msg || error.message || 'Failed to upload attachment.';
                setReplyAttachmentError(message);
            } finally {
                setReplyAttachmentUploading(false);
            }
        },
        [replyAttachments.length, uploadAttachment]
    );

    const handleRemoveReplyAttachment = useCallback((url) => {
        setReplyAttachments((prev) => prev.filter((attachment) => attachment.url !== url));
    }, []);

    const statusCounts = useMemo(() => ({
        new: stats?.status?.new || 0,
        open: stats?.status?.open || 0,
        pending: stats?.status?.pending || 0,
        resolved: stats?.status?.resolved || 0,
        closed: stats?.status?.closed || 0,
    }), [stats]);

    const fetchStats = useCallback(async () => {
        if (!token) return;
        setStatsLoading(true);
        try {
            const { data } = await axios.get('/api/support/tickets/stats', { headers: authHeaders });
            setStats(data || { status: {}, priority: {}, total: 0 });
        } catch (error) {
            console.error('Failed to load ticket stats', error);
        } finally {
            setStatsLoading(false);
        }
    }, [token, authHeaders]);

    const fetchTickets = useCallback(async () => {
        if (!token) return;
        setTicketsLoading(true);
        setTicketsError('');
        try {
            const params = {};
            if (filters.status !== 'all') params.status = filters.status;
            if (filters.priority !== 'all') params.priority = filters.priority;
            if (filters.search) params.search = filters.search;
            if (filters.assigned === 'mine' && user?._id) {
                params.assignedTo = user._id;
            } else if (filters.assigned === 'unassigned') {
                params.assignedTo = 'unassigned';
            }
            const { data } = await axios.get('/api/support/tickets', { headers: authHeaders, params });
            setTickets(data?.tickets || []);
        } catch (error) {
            console.error('Failed to load tickets', error);
            setTicketsError(error?.response?.data?.msg || 'Unable to load support tickets.');
        } finally {
            setTicketsLoading(false);
        }
    }, [token, authHeaders, filters.status, filters.priority, filters.search, filters.assigned, user?._id]);

    const fetchTicketDetails = useCallback(async (ticketId) => {
        if (!token || !ticketId) return;
        setThreadLoading(true);
        setThreadError('');
        try {
            const { data } = await axios.get(`/api/support/tickets/${ticketId}`, {
                headers: authHeaders,
                params: { includeInternal: true },
            });
            setSelectedTicket(data.ticket || null);
            setThread(data.replies || []);
            if (data?.ticket) {
                setStatusUpdate(data.ticket.status);
                setPriorityUpdate(data.ticket.priority);
            }
        } catch (error) {
            console.error('Failed to load ticket details', error);
            setThreadError(error?.response?.data?.msg || 'Unable to load ticket details.');
        } finally {
            setThreadLoading(false);
        }
    }, [token, authHeaders]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    useEffect(() => {
        setReplyAttachments([]);
        setReplyAttachmentError('');
        setReplyAttachmentUploading(false);
    }, [selectedTicketId]);

    useEffect(() => {
        if (!selectedTicketId && tickets.length > 0) {
            setSelectedTicketId(tickets[0].id);
            return;
        }
        if (selectedTicketId) {
            fetchTicketDetails(selectedTicketId);
        } else {
            setSelectedTicket(null);
            setThread([]);
        }
    }, [selectedTicketId, tickets.length, fetchTicketDetails]);

    const handleApplySearch = () => {
        setFilters((prev) => ({ ...prev, search: searchDraft.trim() }));
    };

    const handleClearFilters = () => {
        setFilters({ status: 'all', priority: 'all', assigned: 'all', search: '' });
        setSearchDraft('');
    };

    const handleRefresh = () => {
        fetchTickets();
        fetchStats();
        if (selectedTicketId) {
            fetchTicketDetails(selectedTicketId);
        }
    };

    const handleSendReply = async () => {
        if (!token || !selectedTicketId) return;
        if (!replyMessage.trim()) {
            setReplyError('Please enter a reply message.');
            return;
        }
        if (replyAttachmentUploading) {
            setReplyError('Please wait for attachments to finish uploading.');
            return;
        }
        setReplySubmitting(true);
        setReplyError('');
        try {
            const payload = {
                message: replyMessage.trim(),
                internal: internalNote,
            };
            if (replyAttachments.length) {
                payload.attachments = replyAttachments;
            }
            if (statusUpdate && selectedTicket && statusUpdate !== selectedTicket.status) {
                payload.status = statusUpdate;
            }
            if (priorityUpdate && selectedTicket && priorityUpdate !== selectedTicket.priority) {
                payload.priority = priorityUpdate;
            }
            if (assignToSelf) {
                payload.assignToSelf = true;
            }
            const { data } = await axios.post(`/api/support/tickets/${selectedTicketId}/replies`, payload, {
                headers: authHeaders,
            });
            if (data?.reply) {
                setThread((prev) => [...prev, data.reply]);
            }
            if (data?.ticket) {
                setSelectedTicket(data.ticket);
                setTickets((prev) => prev.map((ticket) => (ticket.id === data.ticket.id ? data.ticket : ticket)));
                setStatusUpdate(data.ticket.status);
                setPriorityUpdate(data.ticket.priority);
                fetchStats();
            }
            setReplyMessage('');
            setAssignToSelf(false);
            setInternalNote(false);
            setReplyAttachments([]);
            setReplyAttachmentError('');
        } catch (error) {
            const message = error.response?.data?.msg || 'Failed to send reply.';
            setReplyError(message);
        } finally {
            setReplySubmitting(false);
        }
    };

    const handleUpdateTicket = async () => {
        if (!token || !selectedTicketId || !selectedTicket) return;
        const payload = {};
        if (statusUpdate && statusUpdate !== selectedTicket.status) {
            payload.status = statusUpdate;
        }
        if (priorityUpdate && priorityUpdate !== selectedTicket.priority) {
            payload.priority = priorityUpdate;
        }
        if (!Object.keys(payload).length) return;
        setActionLoading(true);
        setActionError('');
        try {
            const { data } = await axios.patch(`/api/support/tickets/${selectedTicketId}`, payload, {
                headers: authHeaders,
            });
            if (data?.ticket) {
                setSelectedTicket(data.ticket);
                setTickets((prev) => prev.map((ticket) => (ticket.id === data.ticket.id ? data.ticket : ticket)));
                fetchStats();
            }
        } catch (error) {
            setActionError(error.response?.data?.msg || 'Failed to update ticket.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAssignSelf = async () => {
        if (!token || !selectedTicketId || !user?._id) return;
        setActionLoading(true);
        setActionError('');
        try {
            const { data } = await axios.patch(`/api/support/tickets/${selectedTicketId}`, {
                assignedTo: user._id,
            }, { headers: authHeaders });
            if (data?.ticket) {
                setSelectedTicket(data.ticket);
                setTickets((prev) => prev.map((ticket) => (ticket.id === data.ticket.id ? data.ticket : ticket)));
            }
        } catch (error) {
            setActionError(error.response?.data?.msg || 'Failed to assign ticket.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUnassign = async () => {
        if (!token || !selectedTicketId) return;
        setActionLoading(true);
        setActionError('');
        try {
            const { data } = await axios.patch(`/api/support/tickets/${selectedTicketId}`, {
                assignedTo: 'unassigned',
            }, { headers: authHeaders });
            if (data?.ticket) {
                setSelectedTicket(data.ticket);
                setTickets((prev) => prev.map((ticket) => (ticket.id === data.ticket.id ? data.ticket : ticket)));
            }
        } catch (error) {
            setActionError(error.response?.data?.msg || 'Failed to unassign ticket.');
        } finally {
            setActionLoading(false);
        }
    };

    const activeTicketCount = (status) => statusCounts[status] || 0;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, pb: 6 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <SupportAgentIcon color="primary" />
                    <Typography variant="h5" fontWeight={700}>
                        Support ticket queue
                    </Typography>
                </Stack>
                <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e0e0e0', mb: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                        {STATUS_OPTIONS.map((status) => (
                            <Chip
                                key={status}
                                label={`${status.charAt(0).toUpperCase() + status.slice(1)} (${activeTicketCount(status)})`}
                                color={filters.status === status ? 'primary' : statusChipColor[status] || 'default'}
                                onClick={() => setFilters((prev) => ({ ...prev, status }))}
                            />
                        ))}
                        {statsLoading && <CircularProgress size={18} sx={{ ml: 0.5 }} />}
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
                        <Select
                            size="small"
                            value={filters.priority}
                            onChange={(event) => setFilters((prev) => ({ ...prev, priority: event.target.value }))}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="all">All priorities</MenuItem>
                            {PRIORITY_OPTIONS.map((priority) => (
                                <MenuItem key={priority} value={priority}>
                                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                        <Select
                            size="small"
                            value={filters.assigned}
                            onChange={(event) => setFilters((prev) => ({ ...prev, assigned: event.target.value }))}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="all">All assignments</MenuItem>
                            <MenuItem value="mine">Assigned to me</MenuItem>
                            <MenuItem value="unassigned">Unassigned</MenuItem>
                        </Select>
                        <TextField
                            size="small"
                            label="Search tickets"
                            value={searchDraft}
                            onChange={(event) => setSearchDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') handleApplySearch();
                            }}
                            sx={{ minWidth: 220 }}
                        />
                        <Button variant="contained" onClick={handleApplySearch}>
                            Apply
                        </Button>
                        <Button color="inherit" onClick={handleClearFilters}>
                            Clear
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
                            <Typography variant="h6">Ticket list</Typography>
                            {ticketsLoading && <CircularProgress size={20} />}
                        </Stack>
                        <Divider />
                        {ticketsError && (
                            <Alert severity="error" sx={{ m: 2 }}>
                                {ticketsError}
                            </Alert>
                        )}
                        {!ticketsLoading && tickets.length === 0 ? (
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No tickets match the current filters.
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ maxHeight: 520, overflowY: 'auto' }}>
                                {tickets.map((ticket) => (
                                    <ListItem disablePadding key={ticket.id}>
                                        <ListItemButton
                                            selected={ticket.id === selectedTicketId}
                                            onClick={() => setSelectedTicketId(ticket.id)}
                                            alignItems="flex-start"
                                            sx={{ py: 1.5 }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="subtitle2" noWrap>
                                                            {ticket.subject}
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            label={ticket.status}
                                                            color={statusChipColor[ticket.status] || 'default'}
                                                        />
                                                    </Stack>
                                                }
                                                secondary={
                                                    <Stack spacing={0.5}>
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            #{ticket.ticketNumber} • {ticket.priority} priority
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {ticket.requester?.name || ticket.requester?.email || 'Unknown requester'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            Updated {formatDateTime(ticket.lastActivityAt)}
                                                        </Typography>
                                                    </Stack>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0', minHeight: 620 }}>
                        {threadLoading ? (
                            <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                                <CircularProgress />
                            </Stack>
                        ) : !selectedTicket ? (
                            <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }} spacing={2}>
                                <SupportAgentIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
                                <Typography variant="h6">Select a ticket to view details</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Choose a conversation from the left to review the full history and respond.
                                </Typography>
                            </Stack>
                        ) : (
                            <>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                                    <Box>
                                        <Typography variant="h6">{selectedTicket.subject}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Ticket #{selectedTicket.ticketNumber} • Created {formatDateTime(selectedTicket.createdAt)}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
                                        <Chip label={selectedTicket.status} color={statusChipColor[selectedTicket.status] || 'default'} />
                                        <Chip label={`${selectedTicket.priority} priority`} color={priorityChipColor[selectedTicket.priority] || 'default'} />
                                    </Stack>
                                </Stack>

                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} mt={3}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Requester
                                        </Typography>
                                        <Typography variant="body2">{selectedTicket.requester?.name || 'Unknown user'}</Typography>
                                        {selectedTicket.requester?.email && (
                                            <Typography variant="caption" color="text.secondary">
                                                {selectedTicket.requester.email}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Assigned to
                                        </Typography>
                                        <Typography variant="body2">
                                            {selectedTicket.assignedTo?.name || 'Unassigned'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Last activity
                                        </Typography>
                                        <Typography variant="body2">{formatDateTime(selectedTicket.lastActivityAt)}</Typography>
                                    </Box>
                                </Stack>

                                {selectedTicket.tags?.length > 0 && (
                                    <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                                        {selectedTicket.tags.map((tag) => (
                                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                                        ))}
                                    </Stack>
                                )}

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={3} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                    <Select
                                        size="small"
                                        value={statusUpdate}
                                        onChange={(event) => setStatusUpdate(event.target.value)}
                                        sx={{ minWidth: 180 }}
                                    >
                                        {STATUS_OPTIONS.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {status.charAt(0).toUpperCase() + status.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        size="small"
                                        value={priorityUpdate}
                                        onChange={(event) => setPriorityUpdate(event.target.value)}
                                        sx={{ minWidth: 160 }}
                                    >
                                        {PRIORITY_OPTIONS.map((priority) => (
                                            <MenuItem key={priority} value={priority}>
                                                {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <Stack direction="row" spacing={1} ml={{ sm: 'auto' }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AssignmentIndIcon />}
                                            onClick={handleAssignSelf}
                                            disabled={actionLoading || !user?._id}
                                        >
                                            Assign to me
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="inherit"
                                            startIcon={<RemoveCircleOutlineIcon />}
                                            onClick={handleUnassign}
                                            disabled={actionLoading}
                                        >
                                            Unassign
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleUpdateTicket}
                                            disabled={actionLoading}
                                        >
                                            Update
                                        </Button>
                                    </Stack>
                                </Stack>
                                {actionError && (
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        {actionError}
                                    </Alert>
                                )}

                                <Divider sx={{ my: 3 }} />

                                {threadError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {threadError}
                                    </Alert>
                                )}

                                <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 1 }}>
                                    {thread.map((reply) => (
                                        <ReplyBubble key={reply.id} reply={reply} />
                                    ))}
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                <Stack spacing={1.5}>
                                    {replyError && <Alert severity="error">{replyError}</Alert>}
                                    <TextField
                                        label={internalNote ? 'Internal note' : 'Reply to requester'}
                                        value={replyMessage}
                                        onChange={(event) => setReplyMessage(event.target.value)}
                                        multiline
                                        minRows={4}
                                        fullWidth
                                        placeholder={internalNote ? 'Add a note visible only to agents' : 'Type your response to the requester'}
                                    />
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                component="label"
                                                startIcon={<AttachFileIcon />}
                                                disabled={
                                                    replyAttachmentUploading ||
                                                    replyAttachments.length >= MAX_ATTACHMENTS
                                                }
                                            >
                                                {replyAttachmentUploading ? 'Uploading…' : 'Add attachments'}
                                                <input
                                                    type="file"
                                                    hidden
                                                    multiple
                                                    onChange={handleReplyAttachmentChange}
                                                />
                                            </Button>
                                            {replyAttachmentUploading && <CircularProgress size={20} />}
                                            <Typography variant="caption" color="text.secondary">
                                                Up to {MAX_ATTACHMENTS} files, 20MB each.
                                            </Typography>
                                        </Stack>
                                        {replyAttachmentError && (
                                            <Alert severity="error">{replyAttachmentError}</Alert>
                                        )}
                                        {replyAttachments.length > 0 && (
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {replyAttachments.map((attachment) => (
                                                    <Chip
                                                        key={attachment.url}
                                                        component="a"
                                                        href={attachment.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        label={attachment.name}
                                                        clickable
                                                        onDelete={() => handleRemoveReplyAttachment(attachment.url)}
                                                        sx={{ maxWidth: 260 }}
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                    </Stack>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={internalNote}
                                                    onChange={(event) => setInternalNote(event.target.checked)}
                                                />
                                            }
                                            label="Internal note"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={assignToSelf}
                                                    onChange={(event) => setAssignToSelf(event.target.checked)}
                                                />
                                            }
                                            label="Assign to me"
                                        />
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Button
                                            variant="contained"
                                            endIcon={<SendIcon />}
                                            onClick={handleSendReply}
                                            disabled={replySubmitting || replyAttachmentUploading}
                                        >
                                            {replySubmitting ? 'Sending...' : internalNote ? 'Add note' : 'Send reply'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AdminSupportTickets;
